import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { TableModule } from '@openng/optimus-ui/table';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';
import { CommonModule } from '@angular/common';
import { GlobalTemplateService } from '../../../../services/global-template.service';
import {
  catchError,
  EMPTY,
  map,
  of,
  mergeMap,
  take,
  tap,
  Subject,
  takeUntil,
} from 'rxjs';
import { TranslateService } from '../../../../services/translate.service';
import { Helper } from '../../../../utils/helper';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ToastBarService } from '../../../../services/toast-bar.service';
import { Router } from '@angular/router';
import { TranslatingTemplateStatus } from '../../../../models/email-template/translatingTemplateStatus.interface';

export interface TranslateTemplate {
  language: string;
  link: string;
  translated?: boolean;
}

@Component({
    selector: 'app-translate-list',
    imports: [TableModule, SelectButtonModule, CommonModule, ButtonModule],
    templateUrl: './translate-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './translate-list.component.scss'
})
export class TranslateComponent implements OnDestroy {
  templates: TranslateTemplate[] = [];
  templateName: string | undefined;
  templateData: TranslatingTemplateStatus | undefined;
  disableApproveTranslation: boolean = true;
  cachedTemplate: any | undefined;
  idcustom: string | undefined;

  private _destroy$ = new Subject<void>();

  constructor(
    private globalTemplateService: GlobalTemplateService,
    private translateService: TranslateService,
    private toastBarService: ToastBarService,
    private router: Router
  ) {
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  ngOnInit() {
    this.globalTemplateService.cachedTemplateData$
      .pipe(
        take(1),
        // Set template data
        tap((cachedTemplate) => this.setDataFromCachedTemplate(cachedTemplate)),
        mergeMap(() => this.checkIfAlreadyApproved()),
        mergeMap(() => this.fetchAndPopulateTemplates()),
        mergeMap((hasExistingTemplates) =>
          hasExistingTemplates
            ? EMPTY
            : this.createInitialTemplates(this.cachedTemplate, this.idcustom!)
        ),
        catchError((err) => {
          console.error('Error in template initialization:', err);
          return EMPTY;
        })
      )
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  checkIfAlreadyApproved() {
    return this.translateService
      .checkIfApprovedTemplate(this.templateName!)
      .pipe(
        tap((isApproved) => {
          if (isApproved) {
            this.toastBarService.showToast({
              severity: 'error',
              summary: 'Error',
              detail: `Template is already approved!`,
            });
            this.router.navigate(['/']);
          }
        })
      );
  }

  setDataFromCachedTemplate(cachedTemplate: any) {
    this.cachedTemplate = cachedTemplate;
    this.templateName = cachedTemplate.templateName;
    this.idcustom = Helper.generateIdHash(
      `${this.cachedTemplate.templateName}-en`
    );
  }

  /**
   * Handles the retrieval and initialization of template data for translation.
   *
   * This method generates a unique ID for the given cached template, then attempts to fetch
   * existing templates by the template's name. If existing templates are found, it populates
   * the templates table with them. If not, it creates initial templates using the provided
   * cached template and generated ID. Any errors encountered during the process are caught
   * and logged, and the observable completes without emitting further values.
   *
   * @param cachedTemplate - The template data object to process.
   * @returns An Observable that completes after processing the template data, or emits no values if templates already exist or an error occurs.
   */
  private fetchAndPopulateTemplates() {
    return this.translateService
      .getTemplatesByTemplateName(this.cachedTemplate.templateName)
      .pipe(
        map((existingTemplates: TranslatingTemplateStatus[]) => {
          if (this.hasExistingTemplates(existingTemplates)) {
            this.populateTemplatesTable(existingTemplates);
            this.checkIfAllTranslated(existingTemplates);
            return true;
          } else {
            return false;
          }
        }),
        catchError((err) => {
          console.warn('Error found while getting templates:', err);
          return EMPTY;
        })
      );
  }

  /**
   * Checks if all templates in the provided array have been translated.
   * Sets the `disableApproveTranslation` property to `true` if any template is not translated,
   * otherwise sets it to `false`.
   *
   * @param existingTemplates - An array of `TranslatingTemplateStatus` objects to check for translation status.
   */
  private checkIfAllTranslated(existingTemplates: TranslatingTemplateStatus[]) {
    this.disableApproveTranslation = existingTemplates.some(
      (template) => template.translated === false
    );
  }

  private hasExistingTemplates(templates: TranslateTemplate[]): boolean {
    return templates != null && templates.length > 0;
  }

  /**
   * Populate the table with template content
   * @param templates - templates data for the table
   */
  private populateTemplatesTable(templates: TranslatingTemplateStatus[]): void {
    templates.map((template) => {
      this.templates.push(template);
    });
  }

  /**
   * Creates the intital translation templates.
   * @param cachedTemplate - template on which the translation templates will be based
   * @param idcustom - idcustom for the english language
   * @returns
   */
  private createInitialTemplates(cachedTemplate: any, idcustom: string) {
    return this.translateService
      .createInitialTemplates({
        templateName: cachedTemplate.templateName,
        idcustom: idcustom,
        language: cachedTemplate.language,
        templateData: cachedTemplate.blocks,
        templateType: cachedTemplate.templateType
      })
      .pipe(
        tap((res) => {
          // console.log('Templates created:', res);

          // Populate the table with the response
          this.populateTemplatesTable(res);
        }),
        catchError((err) => {
          console.error('Error while creating templates:', err);
          return EMPTY;
        })
      );
  }

  approveTranslations() {
    this.translateService
      .approveTranslations(this.templateName!)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.toastBarService.showToast({
            severity: 'success',
            summary: 'Success',
            detail: `Approved all translations!`,
          });

          this.router.navigate(['/']);
        },
        error: (err) => {
          console.error('Error while approving translations!', err);
          this.toastBarService.showToast({
            severity: 'error',
            summary: 'Error',
            detail: `Error while approving translations!`,
          });
        },
      });
  }

  copyToClipboard(link: string) {
    // Copy the text inside the text field
    navigator.clipboard.writeText(link);
    this.toastBarService.showToast({
      severity: 'success',
      summary: 'Success',
      detail: `Copied the link to clipboard!`,
    });
  }
}
